import { Component, Input, OnInit } from '@angular/core';
import {
  FormGroup,
  FormControl,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MaterialModule } from '@reenstudio/app/material.module';
import { NgFor, NgIf } from '@angular/common';
import { SiteApiService } from '@reenstudio/app/core/services/api/site.services';
import { ActivatedRoute, Router } from '@angular/router';
import { Site } from '@reenstudio/app/core/models/site/site';
import { CerService } from '@reenstudio/app/core/services/api/cer.service';
import { ArchivesService } from '@reenstudio/app/core/services/api/archives.service';
import { concatMap, of, switchMap } from 'rxjs';
import { NGXLogger } from 'ngx-logger';
import { Cer } from '@reenstudio/app/core/models/cer/cer';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { NoDataComponent } from '@reenstudio/app/shared/components/no-data/no-data.component';
import { SnackbarService } from '@reenstudio/app/shared/services/snackbar/snackbar.service';
import {
  SiteAddEditApiRequest,
  SiteAddUserApiRequest,
} from '@reenstudio/app/core/models/api/site/site-api-model';
import { UserAddEditApiRequest } from '@reenstudio/app/core/models/api/user/user-api-model';
import { UserLevel } from '@reenstudio/app/core/enum/user-level';
import { UserApiService } from '@reenstudio/app/core/services/api/user-service';
import { StorageService } from '@reenstudio/app/core/services/storage.service';
import { format } from 'date-fns';
import * as moment from 'moment';

@Component({
  selector: 'app-site-add-edit',
  templateUrl: './site-add-edit.component.html',
  styleUrls: ['./site-add-edit.component.scss'],
  standalone: true,
  imports: [
    MaterialModule,
    FormsModule,
    ReactiveFormsModule,
    NgIf,
    NgFor,
    NgxSkeletonLoaderModule,
    NoDataComponent,
  ],
})
export class SiteAddEditComponent implements OnInit {
  @Input() type: string = 'add';

  loading: boolean = false;

  form: FormGroup;

  site: Site;
  cerList: Cer[];
  consumptionProfiles: any[] = [];
  productionProfiles: any[] = [];
  regions: any[] = [];
  siteId: number;
  userId: string;

  siteTypes: any[] = [];

  latitude: string;
  longitude: string;

  constructor(
    private siteApiService: SiteApiService,
    private route: ActivatedRoute,
    private cerService: CerService,
    private archiveService: ArchivesService,
    private userService: UserApiService,
    private logger: NGXLogger,
    private snackbarService: SnackbarService,
    private router: Router,
    private storageService: StorageService
  ) {}

  ngOnInit(): void {
    this.formInit();
  }

  get f() {
    return this.form.controls;
  }

  formInit() {
    this.loading = true;

    this.form = new FormGroup({
      name: new FormControl('', [Validators.required]),
      email: new FormControl('', [Validators.email]),

      address: new FormControl(''),
      civic: new FormControl(''),
      city: new FormControl(''),
      province: new FormControl(''),
      cap: new FormControl(''),
      latitude: new FormControl(''),
      longitude: new FormControl(''),

      siteActivation: new FormControl('', [Validators.required]),
      systemActivation: new FormControl('', [Validators.required]),
      type: new FormControl('', [Validators.required]),
      cer: new FormControl('', [Validators.required]),
      region: new FormControl('', [Validators.required]),
      consumerProfile: new FormControl(''),
      productionProfile: new FormControl(''),
      incentive: new FormControl(''),
      max_production: new FormControl(''),
      max_consumption: new FormControl(''),
    });

    const params = { enabled: true };

    this.cerService
      .getCers(params)
      .pipe(
        switchMap((cersResponse) => {
          this.logger.debug(
            'Cer for Site Add Edit recevived data',
            cersResponse
          );
          this.cerList = cersResponse.data;
          return this.archiveService.getSiteTypes();
        }),
        switchMap((siteTypesResponse) => {
          this.logger.debug(
            'Site Types for Site Add Edit recevived data',
            siteTypesResponse
          );
          this.siteTypes = siteTypesResponse.data;
          return this.archiveService.getConsumptionProfiles();
        }),
        switchMap((consumptionProfilesResponse) => {
          this.logger.debug(
            'Consumption profiles for Site Add Edit recevived data',
            consumptionProfilesResponse
          );
          this.consumptionProfiles = consumptionProfilesResponse.data;
          return this.archiveService.getRegions();
        }),
        switchMap((regionsResponse) => {
          this.logger.debug(
            'Regions for Site Add Edit recevived data',
            regionsResponse
          );
          this.regions = regionsResponse.data;
          return this.archiveService.getProductionProfiles();
        }),
        switchMap((prouctionProfilesResponse) => {
          this.logger.debug(
            'Production Profiles for Site Add Edit recevived data',
            prouctionProfilesResponse
          );
          this.productionProfiles = prouctionProfilesResponse.data;

          if (this.type === 'edit') {
            return this.route.params.pipe(
              concatMap((params) => {
                this.siteId = params['id'];
                if (this.siteId) {
                  return this.siteApiService.getSite(this.siteId);
                } else {
                  return of(null);
                }
              })
            );
          } else {
            return of(null);
          }
        })
      )
      .subscribe({
        next: (siteData) => {
          if (siteData) {
            this.logger.debug('Site edit received data', siteData);

            const position = JSON.parse(siteData.position_json);
            this.latitude = position.latitude;
            this.longitude = position.longitude;

            this.form.patchValue({
              name: siteData.description,
              address: siteData.address,
              civic: siteData.civic,
              cap: siteData.cap,
              city: siteData.city,
              province: siteData.province,

              latitude: this.latitude,
              longitude: this.longitude,

              siteActivation: siteData.date_activation,
              systemActivation: siteData.system_activation,
              type: siteData.sites_type_id,
              cer: siteData.cer_id,
              region: siteData.region,
              consumerProfile: siteData.consumption_profile_id,
              productionProfile: siteData.production_profile_id,
              incentive: siteData.incentive,
              max_production: siteData.max_production,
              max_consumption: siteData.max_consumption,
            });

            this.siteId = siteData.id;
          }
          this.loading = false;
        },
        error: (err) => {
          console.error('Errore durante le chiamate:', err);
        },
      });
  }

  setFormProfiles(id: string) {
    this.logger.debug('patch Form about profile ', id);
    if (id === 'PRODUCER') {
      this.form.patchValue({
        consumerProfile: new FormControl(''),
        productionProfile: new FormControl('', [Validators.required]),
      });
    } else if (id === 'CONSUMER') {
      this.form.patchValue({
        consumerProfile: new FormControl('', [Validators.required]),
        productionProfile: new FormControl(''),
      });
    } else if (id === 'PROSUMER') {
      this.form.patchValue({
        consumerProfile: new FormControl('', [Validators.required]),
        productionProfile: new FormControl('', [Validators.required]),
      });
    }
  }

  onSubmit() {
    this.loading = true;

    const today = new Date();

    const siteData: SiteAddEditApiRequest = {
      description: this.form.value.name,
      date_activation: this.form.value.siteActivation,
      address: this.form.value.address,
      city: this.form.value.city,
      civic: this.form.value.civic,
      cap: this.form.value.cap,
      province: this.form.value.province,
      region_id: this.form.value.region,
      cer_id: this.form.value.cer,
      sites_type_id: this.form.value.type,
      consumption_profile_id: this.form.value.consumerProfile,
      production_profile_id: this.form.value.productionProfile,
      position_json: JSON.stringify({
        latitude: this.form.value.latitude,
        longitude: this.form.value.longitude,
      }),
      max_production: Number.parseInt(this.form.value.max_production),
      max_consumption: Number.parseInt(this.form.value.max_consumption),
      system_activation: this.form.value.systemActivation,
      cer_activation: moment(today).format('YYYY-MM-DD'),
      incentive: this.form.value.incentive,
      enabled: true,
    };

    const userData: UserAddEditApiRequest = {
      name: this.form.value.name,
      email: this.form.value.email,
      level: UserLevel.SITE,
    };

    this.logger.debug('Site Request Data', siteData);
    this.logger.debug('User Request Data', userData);

    if (this.type === 'add') {
      this.siteApiService
        .addSite(siteData)
        .pipe(
          switchMap((siteResponse) => {
            this.logger.debug('Cer Response', siteResponse);
            this.siteId = siteResponse.details.site.id;
            return this.userService.addUser(userData);
          }),
          switchMap((userResponse) => {
            this.logger.debug('User Response', userResponse);
            this.userId = userResponse.details.user.id;
            const params: SiteAddUserApiRequest = {
              site: this.siteId,
            };
            return this.siteApiService.addSiteUser(this.userId, params);
          }),
          switchMap(() => {
            const params = { enabled: true };
            const sites = this.siteApiService.getSites().subscribe({
              next: (data) => {
                this.storageService.setSites(data.data);
              },
            });
            return of(null);
          })
        )
        .subscribe({
          next: () => {
            this.snackbarService.success('Sito aggiunto con successo');
            this.loading = false;
            this.router.navigate(['/administration/sites']);
          },
          error: (err) => {
            console.error('Errore durante le chiamate:', err);
          },
        });
    } else if (this.type === 'edit') {
      this.siteApiService
        .editSite(this.siteId, siteData)
        .pipe(
          switchMap(() => {
            const params = { enabled: true };
            return this.siteApiService.getSites(params);
          })
        )
        .subscribe({
          next: (data) => {
            this.storageService.setSites(data.data);
            this.snackbarService.success('Sito modificato con successo');
            this.loading = false;
            this.router.navigate(['/administration/sites']);
          },
        });
    }
  }
}
